/**
 * The adaptive music's voices and scheduler (GDD §9; judgment call 49). The score is in src/audio/score.ts; this
 * file plays it on persistent voices (a six-saw pad, a plucked arpeggio with an echo, bass, kick, snare, hi-hats and
 * a driving ostinato), scheduling each sixteenth note 150 ms ahead from the game's frames.
 */
import {
  arpNote,
  bassNote,
  chordNote,
  CYCLE_STEPS,
  driveNote,
  drums,
  HAT,
  HAT_ACCENT,
  KICK,
  layerLevel,
  SNARE,
  STEP_SECS,
  STEPS_PER_BAR,
} from './score.ts';
import { hz } from './tuning.ts';
import { noiseSource, note, silentGain } from './voices.ts';

/** How far ahead notes are scheduled (s): covers a few dropped frames. */
export const LOOKAHEAD = 0.15;

export interface Music {
  readonly pads: OscillatorNode[];
  readonly padFilter: BiquadFilterNode;
  readonly padGain: GainNode;
  readonly arp: OscillatorNode;
  readonly arpGain: GainNode;
  readonly bass: OscillatorNode;
  readonly sub: OscillatorNode;
  readonly bassFilter: BiquadFilterNode;
  readonly bassGain: GainNode;
  readonly kick: OscillatorNode;
  readonly kickGain: GainNode;
  readonly snareGain: GainNode;
  readonly snareTone: GainNode;
  readonly hatGain: GainNode;
  readonly drive: OscillatorNode;
  readonly driveGain: GainNode;
  /** Next sixteenth to schedule and its audio time (0: not started). */
  step: number;
  nextT: number;
  /** Notes scheduled (tests). */
  notes: number;
}

function biquad(ctx: BaseAudioContext, type: BiquadFilterType, f: number, q: number): BiquadFilterNode {
  const b = ctx.createBiquadFilter();
  b.type = type;
  b.frequency.value = f;
  b.Q.value = q;
  return b;
}

function osc(ctx: BaseAudioContext, type: OscillatorType, f: number, detune = 0): OscillatorNode {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = f;
  o.detune.value = detune;
  o.start();
  return o;
}

export function createMusic(ctx: BaseAudioContext, noise: AudioBuffer, dest: AudioNode): Music {
  // Pad: each chord tone on two saws 14 cents apart, through one low-pass.
  const padFilter = biquad(ctx, 'lowpass', 900, 0.8);
  const padGain = silentGain(ctx, dest);
  padFilter.connect(padGain);
  const pads: OscillatorNode[] = [];
  for (let i = 0; i < 3; i++)
    for (const d of [-7, 7]) {
      const o = osc(ctx, 'sawtooth', hz(chordNote(0, i)), d);
      o.connect(padFilter);
      pads.push(o);
    }
  // Arpeggio with a dotted-eighth echo.
  const arp = osc(ctx, 'triangle', 440);
  const arpGain = silentGain(ctx, dest);
  arp.connect(arpGain);
  const echo = ctx.createDelay(1);
  echo.delayTime.value = STEP_SECS * 3;
  const feedback = silentGain(ctx, echo, 0.32);
  const wet = silentGain(ctx, dest, 0.35);
  arpGain.connect(echo);
  echo.connect(feedback);
  echo.connect(wet);
  // Bass: a saw through a resonant low-pass over a sine at the same pitch.
  const bassFilter = biquad(ctx, 'lowpass', 420, 4);
  const bassGain = silentGain(ctx, dest);
  bassFilter.connect(bassGain);
  const bass = osc(ctx, 'sawtooth', 73);
  bass.connect(bassFilter);
  const sub = osc(ctx, 'sine', 73);
  sub.connect(bassGain);
  // Kick: a sine with a falling pitch.
  const kick = osc(ctx, 'sine', 50);
  const kickGain = silentGain(ctx, dest);
  kick.connect(kickGain);
  // Snare: band-passed noise over a short tone; hi-hats: high-passed noise.
  const src = noiseSource(ctx, noise, 1.37);
  const snareBand = biquad(ctx, 'bandpass', 1900, 0.9);
  const snareGain = silentGain(ctx, dest);
  src.connect(snareBand).connect(snareGain);
  const snareOsc = osc(ctx, 'triangle', 185);
  const snareTone = silentGain(ctx, dest);
  snareOsc.connect(snareTone);
  const hatHigh = biquad(ctx, 'highpass', 7500, 0.7);
  const hatGain = silentGain(ctx, dest);
  src.connect(hatHigh).connect(hatGain);
  // The driving ostinato: a saw through a narrow band-pass.
  const drive = osc(ctx, 'sawtooth', 587);
  const driveBand = biquad(ctx, 'bandpass', 1400, 4);
  const driveGain = silentGain(ctx, dest);
  drive.connect(driveBand).connect(driveGain);
  return {
    pads,
    padFilter,
    padGain,
    arp,
    arpGain,
    bass,
    sub,
    bassFilter,
    bassGain,
    kick,
    kickGain,
    snareGain,
    snareTone,
    hatGain,
    drive,
    driveGain,
    step: 0,
    nextT: 0,
    notes: 0,
  };
}

/** Schedules sixteenth `step` of the cycle at audio time `t`, at `intensity` (0–1). */
function playStep(m: Music, step: number, t: number, intensity: number): void {
  const bar = Math.floor(step / STEPS_PER_BAR);
  const s = step % STEPS_PER_BAR;
  if (s === 0) {
    // New chord: the pad glides to it and swells; the filter opens with the intensity.
    for (let i = 0; i < m.pads.length; i++)
      (m.pads[i] as OscillatorNode).frequency.setTargetAtTime(hz(chordNote(bar, i >> 1)), t, 0.05);
    m.padFilter.frequency.setTargetAtTime(700 + 1700 * intensity, t, 0.4);
    m.padGain.gain.setTargetAtTime(0.06, t, 0.35);
    m.padGain.gain.setTargetAtTime(0.04, t + STEP_SECS * 10, 0.6);
    m.notes++;
  }
  const arp = layerLevel(intensity, 'arp');
  const an = arpNote(bar, s);
  if (arp > 0.01 && an >= 0) {
    m.arp.frequency.setValueAtTime(hz(an), t);
    note(m.arpGain.gain, t, 0.11 * arp, 0.004, 0.08);
    m.notes++;
  }
  const bass = layerLevel(intensity, 'bass');
  const bn = bassNote(bar, s, intensity);
  if (bass > 0.01 && bn >= 0) {
    const f = hz(bn);
    m.bass.frequency.setValueAtTime(f, t);
    m.sub.frequency.setValueAtTime(f, t);
    m.bassFilter.frequency.setValueAtTime(300 + 500 * intensity, t);
    note(m.bassGain.gain, t, 0.2 * bass, 0.006, 0.11);
    m.notes++;
  }
  const bits = drums(s, intensity);
  if (bits & KICK) {
    const k = layerLevel(intensity, 'kick');
    m.kick.frequency.setValueAtTime(140, t);
    m.kick.frequency.exponentialRampToValueAtTime(42, t + 0.12);
    note(m.kickGain.gain, t, 0.55 * k, 0.002, 0.07);
    m.notes++;
  }
  if (bits & SNARE) {
    const k = layerLevel(intensity, 'snare');
    note(m.snareGain.gain, t, 0.3 * k, 0.001, 0.045);
    note(m.snareTone.gain, t, 0.12 * k, 0.001, 0.03);
    m.notes++;
  }
  if (bits & (HAT | HAT_ACCENT)) {
    const k = layerLevel(intensity, 'hats');
    if (bits & HAT_ACCENT) note(m.hatGain.gain, t, 0.1 * k, 0.001, 0.02);
    else note(m.hatGain.gain, t, 0.06 * k, 0.001, 0.012);
    m.notes++;
  }
  const drive = layerLevel(intensity, 'drive');
  if (drive > 0.01) {
    m.drive.frequency.setValueAtTime(hz(driveNote(bar, s)), t);
    note(m.driveGain.gain, t, 0.05 * drive, 0.003, 0.04);
    m.notes++;
  }
}

/** Schedules every sixteenth due in the next LOOKAHEAD seconds; after a suspend or a stall, picks up from now. */
export function scheduleMusic(m: Music, now: number, intensity: number): void {
  if (m.nextT < now - 0.05) m.nextT = now + 0.03;
  while (m.nextT < now + LOOKAHEAD) {
    playStep(m, m.step, m.nextT, intensity);
    m.nextT += STEP_SECS;
    m.step = (m.step + 1) % CYCLE_STEPS;
  }
}
